import React, { useRef, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Printer, Download } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { getReleaseOrderDetails } from "@/redux/ImportBooking/actions";
import { getMasterCompanyDetails } from "@/redux/Common/actions";
import { showToast } from "@/redux/Toast/actions";
import { baseURL } from "@/config/APIConfig";

/** Resolve API fields whether camelCase or PascalCase. */
const pickStr = (obj, ...keys) => {
    if (!obj) return "";
    for (const k of keys) {
        const v = obj[k];
        if (v !== undefined && v !== null && String(v).trim() !== "") return String(v).trim();
    }
    return "";
};

let cachedApiOrigin = "";
const getApiOrigin = () => {
    if (cachedApiOrigin) return cachedApiOrigin;
    try {
        cachedApiOrigin = new URL(baseURL.live).origin;
    } catch {
        cachedApiOrigin = "";
    }
    return cachedApiOrigin;
};

/** Logo URLs are often `/Uploads/...` on the API host — iframe print needs absolute URLs + `<base>`. */
const resolveMediaUrl = (src) => {
    if (!src || typeof src !== "string") return "";
    const t = src.trim();
    if (!t) return "";
    if (/^(?:https?:|data:|blob:)/i.test(t)) return t;
    if (t.startsWith("//")) return `${window.location.protocol}${t}`;
    const path = t.startsWith("/") ? t : `/${t}`;
    const appOrigin = window.location.origin || "";
    const apiOrigin = getApiOrigin();
    const uploadedAsset = /^\/uploads\//i.test(path);
    if (uploadedAsset && apiOrigin && apiOrigin !== appOrigin) {
        return `${apiOrigin}${path}`;
    }
    return `${appOrigin}${path}`;
};

/** Sample doc: "4 NOV 2025" */
const formatRoDate = (dateString) => {
    if (!dateString) return "";
    return moment(dateString).isValid()
        ? moment(dateString).format("D MMM YYYY").toUpperCase()
        : "";
};

const getAllStyles = () => {
    let styles = "";

    for (let i = 0; i < document.styleSheets.length; i++) {
        const sheet = document.styleSheets[i];
        try {
            const rules = sheet.cssRules;
            for (let j = 0; j < rules.length; j++) {
                styles += rules[j].cssText;
            }
        } catch (err) {
            // Ignore CORS stylesheets
        }
    }

    return styles;
};

const waitForIframeImages = (doc) =>
    Promise.all(
        Array.from(doc.images || []).map(
            (img) =>
                img.complete && img.naturalHeight !== 0
                    ? Promise.resolve()
                    : new Promise((resolve) => {
                        img.onload = resolve;
                        img.onerror = resolve;
                    })
        )
    );

const ReleaseOrderPrint = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const {
        countryList,
        documentTypePurchaseList,
        documentTypeSalesList,
        incoTermsList,
        quantityTypeList,
        salesPersonList,
        uomTypeList,
        commodityGroupList,
        containerTypeList,
        operatorList,
        moveTypeList,
        currency_list,
        charges_basis_list,
        MasterCompanyDetails: reduxCompanyDetails,
    } = useSelector((state) => state.common);


    const { id: encodedData } = useParams();

    const queryParams = new URLSearchParams(location.search);

    const decode = (val) => {
        try {
            return val ? atob(val) : null;
        } catch {
            return null;
        }
    };

    const bookingid = decode(encodedData);
    const rawFileRef = queryParams.get("fileRefNumber");

    const fileRefNumber = (() => {
        if (!rawFileRef || rawFileRef === "null") return "";
        try {
            return decodeURIComponent(rawFileRef);
        } catch {
            return rawFileRef;
        }
    })();

    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);

    const printRef = useRef();
    const authData = localStorage.getItem("authUser");

    let authUser = {};

    try {
        const parsed = JSON.parse(authData || "[{}]");
        authUser = Array.isArray(parsed) ? parsed[0] : {};
    } catch {
        authUser = {};
    }
    const storedCompanyDetails = (() => {
        try {
            const parsed = JSON.parse(localStorage.getItem("MasterCompanyDetails") || "[]");
            if (Array.isArray(parsed)) return parsed;
            if (parsed && typeof parsed === "object") return [parsed];
            return [];
        } catch {
            return [];
        }
    })();

    const MasterCompanyDetails =
        reduxCompanyDetails?.length > 0
            ? reduxCompanyDetails
            : storedCompanyDetails;

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            try {
                await dispatch(getMasterCompanyDetails(authUser?.companyId));
                if (bookingid && bookingid !== "0") {
                    await _getBooking();
                }
            } catch (error) {
                console.error("Error initializing booking:", error);
                dispatch(showToast("Failed to load Release order details", "error"));
            } finally {
                setLoading(false);
            }
        };

        init();
    }, []);
    // useEffect(() => {
    //     console.log("MasterCompanyDetails Updated:", MasterCompanyDetails);
    // }, [MasterCompanyDetails]);


    const _getBooking = async () => {
        try {
            const response = await dispatch(getReleaseOrderDetails(bookingid, authUser?.companyId));
            if (response) setFormData(response);
        } catch (error) {
            console.error("Error fetching booking:", error);
        }
    };





    const handlePrint = () => {
        if (!printRef.current) return;

        const printContent = printRef.current.innerHTML;
        const styles = getAllStyles();

        const iframe = document.createElement("iframe");
        iframe.style.position = "fixed";
        iframe.style.right = "0";
        iframe.style.bottom = "0";
        iframe.style.width = "0";
        iframe.style.height = "0";
        iframe.style.border = "0";

        document.body.appendChild(iframe);

        const doc = iframe.contentDocument || iframe.contentWindow.document;

        const origin = window.location.origin || "";

        const html = `
    <!DOCTYPE html>
    <html>
    <head>
        <title>Print</title>
        <meta charset="UTF-8" />
        <base href="${origin}/" />
        <style>
            ${styles}

            @page {
                size: A4;
                margin: 10mm;
            }

            html, body {
                height: 100%;
            }

            body {
                 margin: 0;
                    padding: 0;
                    display: flex;
                    flex-direction: column;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
            }

            * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
            }

            /* min-height only — fixed height + flex was shrinking the header off the first printed page */
            .a4-page {
                    width: 210mm;
                    min-height: 297mm;
                    height: auto !important;
                    display: flex;
                    flex-direction: column;
                }

            .a4-page > header {
                    flex-shrink: 0 !important;
                }

            .a4-page > .footer-fix {
                    flex-shrink: 0 !important;
                    margin-top: auto;
                }

              .content {
                    flex: 1;
                }
        </style>
    </head>
    <body>
        ${printContent}
    </body>
    </html>
    `;

        doc.open();
        doc.write(html);
        doc.close();

        const runPrint = () => {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();

            iframe.contentWindow.onafterprint = () => {
                document.body.removeChild(iframe);
            };
        };

        requestAnimationFrame(() => {
            waitForIframeImages(doc)
                .catch(() => { })
                .then(() => new Promise((r) => setTimeout(r, 350)))
                .then(runPrint);
        });
    };
    const handleDownload = async () => {
        if (!printRef.current) return;

        try {
            setDownloading(true);

            const downloadBtn = document.querySelector("[data-download-btn]");
            const originalText = downloadBtn?.textContent;

            if (downloadBtn) {
                downloadBtn.textContent = "Generating PDF...";
                downloadBtn.disabled = true;
            }

            const canvas = await html2canvas(printRef.current, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: "#ffffff",
            });

            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "mm",
                format: "a4",
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            const imgWidth = canvas.width;
            const imgHeight = canvas.height;
            const ratio = imgWidth / imgHeight;

            let width = pageWidth - 20;
            let height = width / ratio;

            if (height > pageHeight - 20) {
                height = pageHeight - 20;
                width = height * ratio;
            }

            const x = (pageWidth - width) / 2;
            const y = (pageHeight - height) / 2;

            pdf.addImage(imgData, "PNG", x, y, width, height);
            const fd = formData || {};
            const refPart =
                fileRefNumber ||
                pickStr(fd, "fileRefNumber", "FileRefNumber", "ourRefNo", "OurRefNo", "releaseOrderNumber", "ReleaseOrderNumber") ||
                "Document";
            pdf.save(`Release_Order_${refPart}_${moment().format("YYYYMMDDHHmmss")}.pdf`);

            if (downloadBtn) {
                downloadBtn.textContent = originalText;
                downloadBtn.disabled = false;
            }
        } catch (error) {
            console.error("Error generating PDF:", error);
            alert("Failed to generate PDF. Please try printing instead.");
            const downloadBtn = document.querySelector("[data-download-btn]");
            if (downloadBtn) {
                downloadBtn.textContent = "Download PDF";
                downloadBtn.disabled = false;
            }
        } finally {
            setDownloading(false);
        }
    };


    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-blue-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading booking data...</p>
                </div>
            </div>
        );
    }

    // if (!formData) {
    //     return (
    //         <div className="flex items-center justify-center min-h-screen bg-gray-100">
    //             <div className="text-center">
    //                 <div className="w-12 h-12 border-4 border-blue-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
    //                 <p className="text-gray-600">Loading booking data...</p>
    //             </div>
    //         </div>
    //     );
    // }

    const fd = formData || {};
    const displayFileRef =
        fileRefNumber ||
        pickStr(fd, "fileRefNumber", "FileRefNumber", "ourRefNo", "OurRefNo") ||
        "";
    const goodsBody =
        [
            pickStr(fd, "cargoDescription", "CargoDescription"),
            pickStr(fd, "natureOfGoods", "NatureOfGoods"),
        ]
            .filter(Boolean)
            .join("\n\n");
    const carrierLine = [
        pickStr(fd, "carrier", "Carrier"),
        pickStr(fd, "CarrierSCAC", "carrierSCAC"),
    ]
        .filter(Boolean)
        .join(" ");
    const vesselVoy = [pickStr(fd, "vessel", "Vessel"), pickStr(fd, "voyage", "Voyage")]
        .filter(Boolean)
        .join(" / ");

    const companyRecord = MasterCompanyDetails?.[0] || {};
    const rawLogo =
        pickStr(companyRecord, "CompanyLogoDark", "companyLogoDark") ||
        pickStr(companyRecord, "CompanyLogo", "companyLogo") ||
        pickStr(companyRecord, "CompanyLogoLight", "companyLogoLight");
    const companyLogoUrl = resolveMediaUrl(rawLogo);
    const companyNameHeader = pickStr(companyRecord, "CompanyName", "companyName");
    const rawProfile = pickStr(
        companyRecord,
        "ProfileAddressPrint",
        "profileAddressPrint",
        "ProfileAddress",
        "profileAddress",
        "CompanyAddress"
    );
    let profileLines = rawProfile
        ? rawProfile.split(",").map((s) => s.trim()).filter(Boolean)
        : [];
    if (!profileLines.length && companyNameHeader) {
        profileLines = [companyNameHeader];
    }

    return (
        <div className="min-h-screen bg-gray-200 print:bg-white text-gray-900 font-sans">

            {/* ACTION BAR */}
            <div className="print:hidden bg-white shadow-sm border-b border-gray-300 sticky top-0 z-10">
                <div className="max-w-4xl mx-auto px-4 py-3 flex justify-center gap-3">
                    <button
                        type="button"
                        data-download-btn
                        onClick={handleDownload}
                        disabled={downloading}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg border"
                    >
                        <Download className="w-4 h-4" />
                        {downloading ? "Generating PDF..." : "Download PDF"}
                    </button>

                    <button
                        type="button"
                        onClick={handlePrint}
                        className="flex items-center gap-2 px-5 py-2 bg-[#1a2744] text-white rounded-lg"
                    >
                        <Printer className="w-4 h-4" />
                        Print
                    </button>
                </div>
            </div>

            {/* A4 CONTAINER */}
            <div className="flex justify-center py-6 print:py-0">
                <div
                    ref={printRef}
                    className="a4-page bg-white shadow-xl print:shadow-none flex flex-col text-black"
                >
                    {/* Must NOT use <header>: index.css hides `header {}` in @media print (ISFPrint uses div role="banner"). */}
                    <div className="ro-print-head shrink-0 px-6 pt-5 pb-2" role="banner">
                        <div className="flex justify-between items-start gap-3">
                            <div className="shrink-0 min-h-[3.5rem] max-w-[200px]">
                                {companyLogoUrl ? (
                                    <img
                                        src={companyLogoUrl}
                                        alt={companyNameHeader || "Company logo"}
                                        className="h-14 max-w-[180px] object-contain object-left block"
                                    />
                                ) : (
                                    <div className="text-[11px] font-bold leading-snug uppercase">
                                        {companyNameHeader || "\u00A0"}
                                    </div>
                                )}
                            </div>
                            <div className="flex-1 flex justify-center px-2 pt-1">
                                <h1 className="text-[17px] font-extrabold tracking-tight text-center uppercase leading-tight">
                                    TURNOVER/RELEASE ORDER
                                </h1>
                            </div>
                            <div className="text-right text-[11px] font-bold max-w-[260px] leading-snug shrink-0 whitespace-pre-line">
                                {profileLines}
                            </div>
                        </div>
                        <div className="flex justify-end mt-3 text-[11px] font-bold">
                            <span>FILE REF NO.: </span>
                            <span className="font-normal ml-1">{displayFileRef || "\u00A0"}</span>
                        </div>
                    </div>

                    {/* BODY */}
                    <div className="flex-1 px-6 pb-2 text-[11px] leading-snug">
                        {/* Section 1 — pickup / release (equal columns, min height when empty) */}
                        <div className="grid grid-cols-2 border border-black">
                            <div className="border-r border-black flex flex-col min-h-[8.5rem]">
                                <div className="px-2 py-2 border-b border-black font-bold uppercase text-[11px]">
                                    CARGO PICKUP LOCATION
                                </div>
                                <div className="px-2 py-2 flex-1 whitespace-pre-line">
                                    {pickStr(fd, "fromLocationAddress", "FromLocationAddress") || "\u00A0"}
                                </div>
                            </div>
                            <div className="flex flex-col min-h-[8.5rem]">
                                <div className="px-2 py-2 border-b border-black font-bold uppercase text-[11px]">
                                    RELEASE TO
                                </div>
                                <div className="px-2 py-2 flex-1 whitespace-pre-line">
                                    {pickStr(fd, "releaseToAddress", "ReleaseToAddress") || "\u00A0"}
                                </div>
                            </div>
                        </div>

                        {/* Section 2 — shipping details (separate bordered block, gap like sample) */}
                        <div className="border border-black mt-3 break-inside-avoid">
                            <div className="grid grid-cols-2 border-b border-black">
                                <div className="p-2 border-r border-black space-y-1">
                                    <div>
                                        <b>M BL# : </b>
                                        {pickStr(fd, "mawbNumber", "MawbNumber", "MAWBNumber") || "\u00A0"}
                                    </div>
                                    <div>
                                        <b>H BL# : </b>
                                        {pickStr(fd, "hawbNumber", "HawbNumber", "HAWBNumber") || "\u00A0"}
                                    </div>
                                    <div>
                                        <b>AMS BL# : </b>
                                        {pickStr(fd, "amsBLNumber", "AmsBLNumber", "AMSBLNumber") || "\u00A0"}
                                    </div>
                                </div>
                                <div className="p-2 space-y-1">
                                    <div>
                                        <b>SUB HOUSE: </b>
                                        {pickStr(fd, "subBLNumber", "SubBLNumber") || "\u00A0"}
                                    </div>
                                    <div>
                                        <b>CARRIER : </b>
                                        {carrierLine || "\u00A0"}
                                    </div>
                                    <div>
                                        <b>VSL / VOY: </b>
                                        {vesselVoy || "\u00A0"}
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 border-b border-black">
                                <div className="p-2 border-r border-black space-y-1">
                                    <div>
                                        <b>PORT OF LOADING : </b>
                                        {pickStr(fd, "portOfLoading", "PortOfLoading") || "\u00A0"}
                                    </div>
                                    <div>
                                        <b>PORT OF DISCHARGE : </b>
                                        {pickStr(fd, "portOfDischarge", "PortOfDischarge") || "\u00A0"}
                                    </div>
                                    <div>
                                        <b>FINAL DESTINATION : </b>
                                        {pickStr(fd, "finalDestination", "FinalDestination") || "\u00A0"}
                                    </div>
                                </div>
                                <div className="p-2 space-y-1">
                                    <div>
                                        <b>E. T. D : </b>
                                        {formatRoDate(fd.etd || fd.Etd || fd.ETD) || "\u00A0"}
                                    </div>
                                    <div>
                                        <b>E. T. A : </b>
                                        {formatRoDate(fd.eta || fd.Eta || fd.ETA) || "\u00A0"}
                                    </div>
                                    <div>
                                        <b>E. T. A : </b>
                                        {formatRoDate(
                                            fd.etaAtDestination || fd.EtaAtDestination || fd.ETAAtDestination
                                        ) || "\u00A0"}
                                    </div>
                                </div>
                            </div>

                            <div className="px-2 py-2 border-b border-black">
                                <b>SHIPPER : </b>
                                <span className="font-normal whitespace-pre-line">
                                    {[
                                        pickStr(fd, "shipper", "Shipper"),
                                        pickStr(fd, "shipperAddress", "ShipperAddress"),
                                    ]
                                        .filter(Boolean)
                                        .join("\n") || "\u00A0"}
                                </span>
                            </div>

                            <div className="px-2 py-2 min-h-[14rem] whitespace-pre-line">
                                <b>DESCRIPTION OF GOODS : </b>
                                <div className="font-normal text-[10px] mt-1">{goodsBody || "\u00A0"}</div>
                            </div>
                        </div>

                        {/* SPECIAL INSTRUCTIONS — below main tables, left-aligned (sample) */}
                        <div className="mt-3 text-[11px]">
                            <p className="font-bold uppercase m-0">SPECIAL INSTRUCTIONS:-</p>
                            <p className="font-normal mt-1 whitespace-pre-line m-0">
                                {pickStr(fd, "specialInstructions", "SpecialInstructions") || "\u00A0"}
                            </p>
                        </div>
                    </div>

                    {/* FOOTER */}
                    <div className="footer-fix shrink-0 text-[10px] mt-auto px-6 pb-3 break-inside-avoid">
                        <div className="grid grid-cols-[2fr_1fr] items-end mb-3 gap-2">
                            <div className="p-2 text-[9px] leading-tight">
                                ALL MERCHANDISE WHEN UNLOADING IS AT CARGO OWNERS RISK OR FIRE, FLOOD, WATER OR OTHER DAMAGES.
                                AS PROVIDING IN CARRIER&apos;S BILL OF LADING, ALL COMMODITIES OF WHICH, BY THIS REFERENCE ARE INCORPORATED HEREIN.
                                MERCHANDISE DUE TO CONSIGNEE&apos;S FAILURE OR DELAY IN TAKING POSSESSION WILL BE STORED AT OWNER&apos;S RISK.
                                CARGO MUST BE AVAILABLE BEFORE CUSTOMS WILL EXAMINE AND RELEASE.
                            </div>
                            <div className="flex flex-col items-end pr-2 pb-1">
                                <div className="w-[200px] border-t border-black mb-1" />
                                <div className="text-[11px] font-semibold uppercase">AUTHORIZED SIGNATURE</div>
                            </div>
                        </div>
                        <div className="text-black text-[9px]">
                            Powered by {MasterCompanyDetails?.[0]?.PoweredBy}
                        </div>
                    </div>
                </div>
            </div>

            {/* PRINT STYLES */}
            <style>{`
      .a4-page {
          width: 210mm;
          min-height: 297mm;
          box-sizing: border-box;
        }
 
        @media screen {
          .a4-page { margin: 0 auto; }
        }
 
        @media print {
          @page { size: A4; margin: 10mm; }
 
          html, body {
            width: 210mm;
            height: 297mm;
          }
 
          body {
            margin: 0;
            padding: 0;
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
 
          .a4-page {
            width: 100%;
            min-height: auto;
            height: auto !important;
            padding: 0;
            margin: 0;
            box-shadow: none !important;
          }

          .a4-page > header {
            flex-shrink: 0 !important;
          }

          .a4-page > .footer-fix {
            flex-shrink: 0 !important;
          }
 
          .print\\:hidden { display: none !important; }
          .print\\:py-0 { padding-top: 0 !important; padding-bottom: 0 !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:bg-white { background: white !important; }
        }
    `}</style>
        </div>
    );

};

export default ReleaseOrderPrint;
